import { ApiError, type Me } from '@smarton-pbx/shared';
import { Delete, Hash, Headphones, History, MicOff, Phone as PhoneIcon, Radio, Settings2, Smartphone, UserRound } from 'lucide-react';
import { type FormEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { api } from '../api';
import { EmptyState, Notice, PageHeader } from '../components/ui';
import { t } from '../i18n';
import { formatNumber, isEmergencyNumber } from '../lib/calls';
import { useLoad } from '../lib/hooks';
import { directions, when } from './Calls';
import { phoneStatusText, usePhone } from './softphone';

const keys: [string, string][] = [
  ['1', ''],
  ['2', 'ABC'],
  ['3', 'DEF'],
  ['4', 'GHI'],
  ['5', 'JKL'],
  ['6', 'MNO'],
  ['7', 'PQRS'],
  ['8', 'TUV'],
  ['9', 'WXYZ'],
  ['*', ''],
  ['0', '+'],
  ['#', ''],
];

export function Phone({ me, setup }: { me: Me; setup: { show: () => void; needsSetup: boolean; blocked: boolean } }) {
  const { status, detail, call, dial, handedOver, failure } = usePhone();
  const [params, setParams] = useSearchParams();
  const [number, setNumberValue] = useState(params.get('number') ?? '');
  // A message about the last thing the person tried: emergency, mobile ringing. (Why a call
  // didn't go through shows on the call card, on every page.)
  const [message, setMessage] = useState<{ kind?: 'error' | 'success'; text: ReactNode; testId: string } | null>(null);
  const [mobileBusy, setMobileBusy] = useState(false);
  const busy = call !== null && call.state !== 'ended';
  const ready = status === 'registered';
  const recent = useLoad(() => api.listCalls({ limit: 6 }).then((page) => page.items));

  const setNumber = (update: string | ((n: string) => string)) => {
    setMessage(null);
    setNumberValue(update);
  };

  // "Call back" from elsewhere puts the number in the dialer once.
  useEffect(() => {
    if (params.has('number')) setParams({}, { replace: true });
  }, [params, setParams]);

  // Like on a mobile phone: holding 0 types "+" instead.
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const heldForPlus = useRef(false);
  const startHold = () => {
    heldForPlus.current = false;
    holdTimer.current = setTimeout(() => {
      heldForPlus.current = true;
      setNumber((n) => n + '+');
    }, 500);
  };
  const endHold = () => {
    if (holdTimer.current) clearTimeout(holdTimer.current);
    holdTimer.current = null;
  };
  const press = (digit: string) => {
    if (digit === '0' && heldForPlus.current) {
      heldForPlus.current = false;
      return;
    }
    setNumber((n) => n + digit);
  };

  // Refresh the list after each call.
  useEffect(() => {
    if (call?.state === 'ended') recent.reload();
  }, [call?.state, recent.reload]);

  /** Emergency numbers aren't connected yet: say so and point to the mobile. */
  function stopEmergency(destination: string): boolean {
    if (!isEmergencyNumber(destination)) return false;
    setMessage({
      kind: 'error',
      text: (
        <>
          <strong>{t('This phone can’t call emergency services.')}</strong> {t('Call {number} from your mobile phone now.', { number: destination })}
        </>
      ),
      testId: 'emergency-notice',
    });
    return true;
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    const destination = number.replace(/[\s()-]/g, '');
    if (!destination || !ready || busy || stopEmergency(destination)) return;
    setMessage(null);
    dial(destination);
  }

  async function callViaMobile() {
    const destination = number.replace(/[\s()-]/g, '');
    if (!destination || stopEmergency(destination)) return;
    setMobileBusy(true);
    setMessage(null);
    try {
      await api.clickToCall({ number: destination });
      setMessage({
        kind: 'success',
        text: t('Your mobile {number} is ringing. Answer it (press 1 if asked) and you’ll be connected.', { number: formatNumber(me.mobileNumber ?? '') }),
        testId: 'mobile-ringing',
      });
      setTimeout(recent.reload, 3000);
    } catch (error) {
      setMessage({
        kind: 'error',
        text: error instanceof ApiError ? error.message : t('The call couldn’t be started. Try again.'),
        testId: 'call-refused',
      });
    } finally {
      setMobileBusy(false);
    }
  }

  return (
    <div>
      <PageHeader title={t('Phone')} description={t('Call a colleague by extension, or any phone number. Use a headset for the best sound.')} />
      {(setup.blocked || setup.needsSetup) && (
        <div className={`setup-banner ${setup.blocked ? 'blocked' : ''}`} data-testid="setup-banner">
          {setup.blocked ? <MicOff size={18} aria-hidden /> : <Settings2 size={18} aria-hidden />}
          <span className="grow">
            {setup.blocked
              ? t('The microphone is blocked in this browser, so calls can’t work.')
              : t('Allow the microphone and notifications so calls work in this browser.')}
          </span>
          <button type="button" className="secondary small" onClick={setup.show}>
            {setup.blocked ? t('How to fix') : t('Set up')}
          </button>
        </div>
      )}
      <div className="phone-layout">
        <form className="card dialer" onSubmit={submit}>
          <div className="dialer-display">
            <input
              inputMode="tel"
              value={number}
              onChange={(e) => setNumber(e.target.value)}
              placeholder={t('Enter a number')}
              aria-label={t('Number or extension')}
              data-testid="destination"
            />
            <button
              type="button"
              className="ghost icon"
              onClick={() => setNumber((n) => n.slice(0, -1))}
              disabled={!number}
              aria-label={t('Delete last digit')}
            >
              <Delete size={20} aria-hidden />
            </button>
          </div>
          <div className="keypad" aria-label={t('Dial pad')}>
            {keys.map(([digit, letters]) => (
              <button
                key={digit}
                type="button"
                onClick={() => press(digit)}
                aria-label={digit === '0' ? t('0, hold for plus') : digit}
                {...(digit === '0' && {
                  onPointerDown: startHold,
                  onPointerUp: endHold,
                  onPointerLeave: endHold,
                  onContextMenu: (event) => event.preventDefault(),
                  title: t('Hold for +'),
                })}
              >
                <span className="digit">{digit}</span>
                <span className="letters">{letters}</span>
              </button>
            ))}
          </div>
          <p className="subtle" style={{ textAlign: 'center', fontSize: '0.85rem', marginTop: '-0.5rem' }}>
            {t('Hold 0 for +, or type the number.')}
          </p>
          <button type="submit" className="call-button" disabled={!ready || busy || !number.trim()} aria-label={t('Call')} data-testid="dial">
            <PhoneIcon size={28} aria-hidden />
          </button>
          {me.mobileNumber && (
            <button
              type="button"
              className="secondary small dialer-secondary"
              disabled={mobileBusy || !number.trim()}
              onClick={() => void callViaMobile()}
              title={t('Your mobile ({number}) rings first, then the number', { number: formatNumber(me.mobileNumber) })}
              data-testid="call-via-mobile"
            >
              <Smartphone size={15} aria-hidden /> {t('Call from my mobile')}
            </button>
          )}
          {message ? (
            <div data-testid={message.testId}>
              <Notice {...(message.kind && { kind: message.kind })}>{message.text}</Notice>
            </div>
          ) : null}
          <p className="subtle" style={{ textAlign: 'center', minHeight: '1.5em' }} data-testid="call-state" data-state={call?.state ?? 'none'}>
            {call?.state === 'ended' && !message && !failure
              ? call.endCause
                ? t('Last call ended ({cause}).', { cause: call.endCause })
                : t('Last call ended.')
              : ''}
          </p>
        </form>

        <div className="stack">
          <div className="info-tiles">
            <div className="card tile">
              <span className="tile-label">
                <Radio size={15} aria-hidden /> {t('Status')}
              </span>
              <span className="tile-value" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <span className={`status-light ${ready ? 'on' : status === 'failed' ? 'off' : 'wait'}`} aria-hidden />
                <span data-testid="phone-status" data-status={status}>
                  {phoneStatusText(status)}
                </span>
              </span>
              {detail && <span className="subtle">{detail}</span>}
              {status === 'other-tab' && <span className="subtle">{handedOver ?? t('Close the other tab to use the phone here.')}</span>}
            </div>
            <div className="card tile">
              <span className="tile-label">
                <Hash size={15} aria-hidden /> {t('Your number')}
              </span>
              <span className="tile-value tabular" data-testid="direct-number">
                {me.directNumber ? formatNumber(me.directNumber) : '—'}
              </span>
              {me.directNumber ? (
                <span className="subtle">{t('Customers see it when you call, and calling it rings you.')}</span>
              ) : me.callOutNumber ? (
                <span className="subtle">{t('You have no number of your own: customers see {number}, your region’s.', { number: formatNumber(me.callOutNumber) })}</span>
              ) : (
                // Calls out would be refused: say so before they dial.
                <span className="field-error" data-testid="no-call-out-number">
                  {t('No number to call out from: calls to outside numbers won’t go through. Ask an administrator.')}
                </span>
              )}
            </div>
            <div className="card tile">
              <span className="tile-label">
                <UserRound size={15} aria-hidden /> {t('Your extension')}
              </span>
              <span className="tile-value tabular">{me.extension}</span>
              <span className="subtle">{t('Colleagues dial this to reach you.')}</span>
            </div>
          </div>
          <div className="card">
            <div className="card-header">
              <h2>{t('Recent calls')}</h2>
            </div>
            {recent.data && recent.data.length === 0 ? (
              <EmptyState icon={History} title={t('No calls yet')}>
                <p>{t('Your calls appear here.')}</p>
              </EmptyState>
            ) : (
              <div>
                {recent.data?.slice(0, 6).map((recentCall) => {
                  const direction = directions[recentCall.direction];
                  const Icon = direction.icon;
                  const other = recentCall.direction === 'INBOUND' ? recentCall.fromNumber : recentCall.toNumber;
                  return (
                    <button key={recentCall.id} type="button" className="recent-call" onClick={() => setNumber(other)} title={t('Put {number} in the dialer', { number: other })}>
                      <span className="avatar square" style={{ width: 30, height: 30, color: direction.color, background: 'var(--surface-2)' }}>
                        <Icon size={16} aria-hidden />
                      </span>
                      <span className="grow">
                        <span className="tabular" style={{ fontWeight: 580 }}>
                          {formatNumber(other)}
                        </span>
                        <span className="subtle" style={{ display: 'block', fontSize: '0.85rem' }}>
                          {direction.label}
                        </span>
                      </span>
                      <span className="subtle">{when(recentCall.startedAt)}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
          <div className="card card-body row" style={{ gap: '0.9rem', flexWrap: 'nowrap', alignItems: 'flex-start' }}>
            <Headphones size={20} className="subtle" aria-hidden />
            <p className="muted">
              {t('Calls ring here while this page is open in your browser. Keep one tab open; if you open a second one, the phone stays in the first.')}{' '}
              {me.mobileNumber
                ? t('If you don’t answer here, your number rings your mobile.')
                : t('If an administrator adds your mobile number, calls you miss here ring it too.')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
